import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWaItem } from '../context/WaItemContext';
import { 
  CLASS_ICONS, 
  IS_DRAFT, 
  IS_WIP, 
  HAS_BLOCK, 
  STATUS_ICONS 
} from '../constants/icons';
import '../styles/components/WaItemDisplay.css';

// Largeur de texte
const TITLE_MAX_LENGTH = 40;
const BLOCK_NAME_MAX_LENGTH = 20;

// Fonction utilitaire pour tronquer le texte
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

// Fonction pour obtenir la couleur de fond selon le label
const getLabelBackgroundColor = (labelId) => {
  try {
    const waManuscriptLabels = localStorage.getItem('wa_manuscript_labels');
    if (!waManuscriptLabels) return 'var(--bg-primary)';
    
    const labels = JSON.parse(waManuscriptLabels);
    const label = labels[labelId];
    return label?.color || 'var(--bg-primary)';
  } catch (error) {
    console.error('Erreur lors de la récupération des labels:', error);
    return 'var(--bg-primary)';
  }
};

// Composant spécialisé pour l'affichage des données WaItem
export const WaItemDisplay = ({ waItem }) => {
  if (!waItem) {
    return (
      <div className="wai-container wai-container--loading wai-container--related">
        <div className="wai-icon-container">
          <FontAwesomeIcon icon={STATUS_ICONS.LOADING} spin className="wai-icon" />
        </div>
        <div className="wai-content">
          <div className="wai-title">Aucune donnée à afficher</div>
        </div>
      </div>
    );
  }

  if (!waItem.isLoaded || !waItem.data) {
    return (
      <div className="wai-container wai-container--error wai-container--related">
        <div className="wai-icon-container">
          <FontAwesomeIcon icon={STATUS_ICONS.ERROR} className="wai-icon" />
        </div>
        <div className="wai-content">
          <div className="wai-title">Erreur de chargement</div>
          <div className="wai-info-line">ID: {waItem.id || 'Non disponible'}</div>
        </div>
      </div>
    );
  }

  const data = waItem.data;
  
  // Déterminer l'icône selon la classe
  const iconDefinition = CLASS_ICONS[data._class] || CLASS_ICONS[data._mainClass] || CLASS_ICONS.default;
  
  // Déterminer les classes CSS selon le rôle et l'état
  const containerClasses = [
    'wai-container',
    waItem.role === 'Main' ? 'wai-container--main' : 'wai-container--related',
    waItem.isDirty ? 'wai-container--dirty' : 'wai-container--clean'
  ].join(' ');

  // Calculer la couleur de fond selon le label
  const backgroundColor = data.label?.id ? getLabelBackgroundColor(data.label.id) : 'var(--bg-primary)';

  return (
    <div className={containerClasses} style={{ backgroundColor }}>
      {/* Carré d'icône */}
      <div className="wai-icon-container" style={{ backgroundColor }}>
        <FontAwesomeIcon icon={iconDefinition} className="wai-icon" />
      </div>
      
      {/* Contenu principal */}
      <div className="wai-content">
        {/* Titre */}
        <div className="wai-title">
          {truncateText(data.title, TITLE_MAX_LENGTH)}
        </div>
        
        {/* Ligne d'informations */}
        <div className="wai-info-line">
          {/* Affichage de _mainClass */}
          {data._mainClass && (
            <span className="wai-main-class">
              {data._mainClass}
            </span>
          )}
        
          {/* Icône isDraft */}
          {data.isDraft !== null && data.isDraft !== undefined && (
            <FontAwesomeIcon icon={IS_DRAFT[data.isDraft.toString()]} className="wai-icon" />
          )}
          
          {/* Icône isWip */}
          {data.isWip !== null && data.isWip !== undefined && (
            <FontAwesomeIcon icon={IS_WIP[data.isWip.toString()]} className="wai-icon" />
          )}
          
          {/* Block info */}
          {data.block && Object.keys(data.block).length > 0 && (
            <>
              <FontAwesomeIcon icon={HAS_BLOCK} className="wai-icon" />
              <span className="wai-block-name">
                {truncateText(data.block.name, BLOCK_NAME_MAX_LENGTH)}
              </span>
            </>
          )}

          {/* Tags belTagNames */}
          {waItem?.belTagNames && waItem.belTagNames.length > 0 && (
            <span className="wai-taglist">
              {waItem.belTagNames.join(' ')}
            </span>
          )}
          
          {/* Séparateur si on a des tags */}
          {((waItem?.belTagNames && waItem.belTagNames.length > 0) || 
            (waItem?.otherTagNames && waItem.otherTagNames.length > 0)) && (
            <span> </span>
          )}
          
          {/* Tags otherTagNames */}
          {waItem?.otherTagNames && waItem.otherTagNames.length > 0 && (
            <span className="wai-taglist-unknown">
              {waItem.otherTagNames.join(' ')}
            </span>
          )}
          
        </div>
      </div>
    </div>
  );
};

// Hook personnalisé pour utiliser WaItemDisplay
export const useWaItemDisplay = () => {
  const { mainWaItem } = useWaItem();
  
  return {
    waItem: mainWaItem,
    WaItemDisplayComponent: ({ children, ...props }) => (
      <WaItemDisplay 
        waItem={mainWaItem} 
        {...props}
      >
        {children}
      </WaItemDisplay>
    )
  };
};
